import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { HardHat, Loader2, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { API_URL, CONDITIONS } from './sous-traitants/constants';

// Radix Select n'accepte pas la valeur vide : sentinelle pour « toujours exigible »
const NO_CONDITION = 'none';

const EMPTY_NEW = { code: '', label: '', obligatoire: false, condition: NO_CONDITION, validite_mois: '', jours_alerte: 30, actif: true };

const normalizeCode = (code) => String(code || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

/**
 * Administration › Sous-traitants : types de documents de conformité exigés des sous-traitants
 * (obligatoire, condition d'exigibilité, validité, délai d'alerte, actif).
 * GET/PUT /api/admin/sous-traitant-document-types
 */
const SousTraitantDocumentTypesConfig = () => {
  const [types, setTypes] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newType, setNewType] = useState({ ...EMPTY_NEW });

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/sous-traitant-document-types`);
        setTypes(res.data?.types || []);
        setDefaults(res.data?.defaults || []);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Chargement des types de documents impossible'));
      } finally {
        setLoading(false);
      }
    };
    fetchTypes();
  }, []);

  const update = (index, field, value) => {
    setTypes((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
    setDirty(true);
  };

  const remove = (index) => {
    const t = types[index];
    if (!window.confirm(`Retirer le type « ${t.label} » ? Les documents déjà déposés restent consultables mais ce type ne sera plus exigé. Vous pouvez plutôt le désactiver.`)) return;
    setTypes((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const add = () => {
    const code = normalizeCode(newType.code);
    const label = newType.label.trim();
    if (!code || !label) { toast.error('Code et libellé requis'); return; }
    if (types.some((t) => t.code === code)) { toast.error('Ce code existe déjà'); return; }
    setTypes((prev) => [...prev, {
      code,
      label,
      description: '',
      obligatoire: !!newType.obligatoire,
      condition: newType.condition === NO_CONDITION ? null : newType.condition,
      validite_mois: newType.validite_mois === '' ? null : Number(newType.validite_mois),
      jours_alerte: Number(newType.jours_alerte) || 30,
      actif: newType.actif !== false
    }]);
    setNewType({ ...EMPTY_NEW });
    setDirty(true);
  };

  const restoreDefaults = () => {
    if (!defaults.length) return;
    if (!window.confirm('Remplacer la configuration actuelle par les types par défaut ? (à enregistrer ensuite)')) return;
    setTypes(defaults.map((t) => ({ ...t })));
    setDirty(true);
  };

  const save = async () => {
    for (const t of types) {
      if (!t.code || !String(t.label || '').trim()) { toast.error('Chaque type doit avoir un code et un libellé'); return; }
    }
    setSaving(true);
    try {
      const payload = types.map((t) => ({
        ...t,
        label: String(t.label).trim(),
        condition: t.condition || null,
        validite_mois: t.validite_mois === '' || t.validite_mois === null || t.validite_mois === undefined ? null : Number(t.validite_mois),
        jours_alerte: Number(t.jours_alerte) || 30,
        actif: t.actif !== false
      }));
      const res = await axios.put(`${API_URL}/admin/sous-traitant-document-types`, { types: payload });
      setTypes(res.data?.types || payload);
      setDirty(false);
      toast.success('Types de documents enregistrés');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur de sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-20"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0F5156]"></div></div>;
  }

  return (
    <Card data-testid="sous-traitant-document-types-config">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><HardHat className="w-4 h-4 text-[#0F5156]" />Documents exigés des sous-traitants</CardTitle>
            <CardDescription className="mt-1">
              Pièces de l'obligation de vigilance (L8222-1 / D8222-5). Les types <span className="font-medium">obligatoires</span> et dont la condition est remplie
              conditionnent le score de conformité. La validité (en mois) calcule la date d'expiration à partir de la date d'émission ; le délai d'alerte déclenche
              les notifications avant expiration.
            </CardDescription>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={restoreDefaults} disabled={saving || !defaults.length}><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Valeurs par défaut</Button>
            <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Enregistrer
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead className="min-w-[220px]">Libellé</TableHead>
                <TableHead className="text-center">Obligatoire</TableHead>
                <TableHead className="min-w-[200px]">Condition d'exigibilité</TableHead>
                <TableHead className="w-28">Validité (mois)</TableHead>
                <TableHead className="w-28">Alerte (jours)</TableHead>
                <TableHead className="text-center">Actif</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {types.map((t, i) => (
                <TableRow key={t.code} className={t.actif === false ? 'opacity-60' : ''}>
                  <TableCell className="font-mono text-xs">{t.code}</TableCell>
                  <TableCell>
                    <Input className="h-8 text-sm" value={t.label || ''} onChange={(e) => update(i, 'label', e.target.value)} />
                    {t.description && <p className="text-[11px] text-slate-500 mt-1">{t.description}</p>}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch checked={!!t.obligatoire} onCheckedChange={(v) => update(i, 'obligatoire', v)} />
                  </TableCell>
                  <TableCell>
                    <Select value={t.condition || NO_CONDITION} onValueChange={(v) => update(i, 'condition', v === NO_CONDITION ? null : v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CONDITIONS).map(([k, v]) => <SelectItem key={k || NO_CONDITION} value={k || NO_CONDITION}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input className="h-8 text-sm" type="number" min="0" step="1" placeholder="—"
                      value={t.validite_mois === null || t.validite_mois === undefined ? '' : t.validite_mois}
                      onChange={(e) => update(i, 'validite_mois', e.target.value === '' ? null : Number(e.target.value))} />
                  </TableCell>
                  <TableCell>
                    <Input className="h-8 text-sm" type="number" min="1" step="1" value={t.jours_alerte ?? 30}
                      onChange={(e) => update(i, 'jours_alerte', e.target.value === '' ? '' : Number(e.target.value))} />
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch checked={t.actif !== false} onCheckedChange={(v) => update(i, 'actif', v)} />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => remove(i)} title="Retirer"><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {types.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500 py-6">Aucun type configuré : ajoutez-en un ou restaurez les valeurs par défaut.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-7 gap-2 items-end p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="space-y-1">
            <Label className="text-xs">Code</Label>
            <Input className="h-8 font-mono text-sm" value={newType.code} onChange={(e) => setNewType({ ...newType, code: e.target.value })} placeholder="carte_btp" />
          </div>
          <div className="space-y-1 lg:col-span-2">
            <Label className="text-xs">Libellé</Label>
            <Input className="h-8 text-sm" value={newType.label} onChange={(e) => setNewType({ ...newType, label: e.target.value })} placeholder="Carte BTP des salariés" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Condition</Label>
            <Select value={newType.condition} onValueChange={(v) => setNewType({ ...newType, condition: v })}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CONDITIONS).map(([k, v]) => <SelectItem key={k || NO_CONDITION} value={k || NO_CONDITION}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Validité (mois)</Label>
            <Input className="h-8 text-sm" type="number" min="0" value={newType.validite_mois} onChange={(e) => setNewType({ ...newType, validite_mois: e.target.value })} placeholder="—" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Alerte (jours)</Label>
            <Input className="h-8 text-sm" type="number" min="1" value={newType.jours_alerte} onChange={(e) => setNewType({ ...newType, jours_alerte: e.target.value })} />
          </div>
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={!!newType.obligatoire} onCheckedChange={(v) => setNewType({ ...newType, obligatoire: v })} />Obligatoire
            </label>
            <Button onClick={add} size="sm" className="bg-[#0F5156] hover:bg-[#0d464a] h-8"><Plus className="w-3 h-3 mr-1" />Ajouter</Button>
          </div>
        </div>
        {dirty && !saving && <p className="text-xs text-amber-600">Modifications non enregistrées.</p>}
        {saving && <p className="text-xs text-slate-500">Enregistrement...</p>}
      </CardContent>
    </Card>
  );
};

export default SousTraitantDocumentTypesConfig;
